import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Dialog, DialogContent, DialogTitle, DialogActions, Button, Stack, Divider, Alert } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { Storage } from '../../../Types/Storage';
import { StorageHealthConfig, StorageHealthSetupInput } from '../../../Types/StorageHealth';
import SetupMode from '../SetupMode';
import HealthIndexOverview, { HealthIndexReason, ScoreBreakdownMap } from './HealthIndexOverview';
import IoTClientSummary, { IoTMainData, IoTCurrentMode } from './IoTClientSummary';
import StorageDetails from './StorageDetails';
import { useNavigate } from 'react-router-dom';

type ExtendedStorageHealthConfig = StorageHealthConfig & { iotClientId?: string | null };

interface HealthIndexMetadata {
  inspectionDate?: string | null;
  bayCount?: number | null;
  co2?: number | null;
  [key: string]: unknown;
}

interface InspectionDataSummary {
  inspectionId?: number | null;
  locationId?: number | null;
  purpose?: string | null;
  co2?: number | null;
  targetCo2?: number | null;
  targetCfm?: number | null;
  inspectionDate?: string | null;
  bays?: { name?: string | null; currentFillLevel?: number | null }[] | null;
  [key: string]: unknown;
}

interface IoTDataSummary {
  mainData?: IoTMainData | null;
  currentMode?: IoTCurrentMode | null;
  timeStamp?: string | null;
  alarmData?: Record<string, unknown> | null;
  dailyFanRun?: Record<string, unknown> | null;
  totalFanRun?: Record<string, unknown> | null;
  dateTimeData?: Record<string, unknown> | null;
  equipStatusData?: Record<string, unknown> | null;
  pgmData?: Record<string, unknown> | null;
  pileTempsData?: Record<string, unknown> | null;
  pileHumidsData?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface HealthIndexDetails {
  finalHealthIndex?: number | null;
  reasons?: HealthIndexReason[];
  metadata?: HealthIndexMetadata | null;
  scoreBreakdown?: ScoreBreakdownMap | null;
  inspectionData?: Record<string, unknown> | null;
}

interface MonitorData {
  config?: ExtendedStorageHealthConfig | null;
  error?: boolean;
  message?: string;
  healthIndex?: HealthIndexDetails | null;
  inspectionData?: InspectionDataSummary | null;
  iotData?: IoTDataSummary | null;
}

interface MonitorModeProps {
  storage: Storage;
  monitorData?: MonitorData | null;
  onUpdate: (data: StorageHealthSetupInput) => Promise<void> | void;
  editing?: boolean;
}

const MonitorMode: React.FC<MonitorModeProps> = ({ storage, monitorData, onUpdate }) => {
  const [openEdit, setOpenEdit] = useState(false);
  const navigate = useNavigate();
  const config = monitorData?.config ?? null;
  const healthIndex = monitorData?.healthIndex ?? null;
  const healthReasons = healthIndex?.reasons ?? [];
  const inspectionDate = healthIndex?.metadata?.inspectionDate ?? null;
  const iotData = monitorData?.iotData ?? null;
  const inspectionId = monitorData?.inspectionData?.inspectionId ?? (healthIndex?.metadata?.inspectionId as number | string | null | undefined) ?? null;
  const locationFilter = useMemo(() => {
    if (!storage?.id) return undefined;
    return [{ id: storage.id, locationName: storage.name }];
  }, [storage]);

  const handleViewHistory = useCallback(() => {
    if (inspectionId === null || inspectionId === undefined) {
      return;
    }
    const sheetType = 'inspections';
    const sheetIdParam = String(inspectionId);
    const searchParams = new URLSearchParams({ sheetType, sheetId: sheetIdParam });
    navigate(
      { pathname: '/work-history', search: `?${searchParams.toString()}` },
      {
        state: {
          sheetType,
          sheetId: sheetIdParam,
          id: inspectionId,
          locations: locationFilter,
        },
      }
    );
  }, [inspectionId, locationFilter, navigate]);

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={3} sx={{ flex: '0 0 auto' }}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Box flex={1} minWidth={0}>
                <HealthIndexOverview
                  storageName={storage.name}
                  finalHealthIndex={healthIndex?.finalHealthIndex ?? null}
                  reasons={healthReasons}
                  inspectionDate={inspectionDate}
                  scoreBreakdown={healthIndex?.scoreBreakdown ?? null}
                  onViewWorkHistory={handleViewHistory}
                  viewWorkHistoryDisabled={inspectionId === null || inspectionId === undefined}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => setOpenEdit(true)}
                aria-label="edit storage health settings"
                sx={{ alignSelf: 'flex-start' }}
              >
                <SettingsIcon />
              </IconButton>
            </Box>

            {monitorData?.error && (
              <Alert severity="warning" variant="outlined">
                {monitorData.message ?? 'We were unable to calculate a health index for this storage.'}
              </Alert>
            )}

            {!monitorData?.inspectionData && !monitorData?.error && (
              <Alert severity="info" variant="outlined">
                No inspection data found for this storage. The health index will be available once an inspection is completed.
              </Alert>
            )}

            <Divider flexItem sx={{ my: 1 }} />
            {iotData ? (
              <Stack spacing={2}>
                <IoTClientSummary
                  mainData={iotData.mainData ?? null}
                  pgmData={iotData.pgmData as any}
                  currentMode={iotData.currentMode ?? null}
                  timeStamp={iotData.timeStamp ?? null}
                  config={config ?? undefined}
                  alarmData={iotData.alarmData as any}
                />
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Live panel status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No real-time panel data is available for this storage yet.
                </Typography>
              </Stack>
            )}
          </Stack>

          <Box sx={{ flex: '1 1 auto' }} />

          <Box sx={{ flex: '0 0 auto', mt: 3 }}>
            <StorageDetails
              purpose={config?.purpose ?? null}
              varieties={(config as any)?.varieties ?? null}
              bays={monitorData?.inspectionData?.bays as any}
              inspectionDate={monitorData?.inspectionData?.inspectionDate ?? (healthIndex?.metadata?.inspectionDate as string | null | undefined) ?? null}
              inspectionId={inspectionId}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Storage Health Settings</DialogTitle>
        <DialogContent>
          <SetupMode
            storage={storage}
            initial={config ?? undefined}
            onSave={async (data) => {
              await onUpdate(data);
              setOpenEdit(false);
            }}
            onCancel={() => setOpenEdit(false)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MonitorMode;
